/**
 * Reply / quoted-history delimiter regexes (quote and thread headers only).
 *
 * **Do not add parallel cut logic.** New patterns belong here; they take effect only via
 * {@link computeEmailVisiblePlainCut} in `email_visible_plain_cut.ts` (inbound plain +
 * HTML anchor). Closing phrases ("Mit freundlichen Grüßen", …) are NOT regex delimiters —
 * they are handled in `email_visible_plain_cut.ts` (phrase line + 4 following lines).
 *
 * LINE_BREAK: CR/LF and Unicode line terminators users perceive as new lines.
 * NOT_LINE / NOT_LINE_NOT_GT: "rest of line" spans that must not cross those boundaries.
 *
 * Includes EXPERIMENTAL rule: any line-start (after LINE_BREAK) containing `<email@domain>`.
 * Glue rules: Outlook HTML→plain may omit `\n` before `Von:`/`From:`; match only when
 * `Gesendet:`/`Sent:` or angle-bracket email follows, with a narrow lookbehind (see tests).
 *
 * Plain-text `>` quoting is NOT a rule here: a bare `>` also marks a sender's own
 * indented paragraphs, so it needs surrounding evidence. {@link QUOTED_LINE_RE} and
 * {@link QUOTE_HEADER_LINE_RE} feed that check in `email_visible_plain_cut.ts`.
 */

/** Regex source fragment: line boundary (CRLF, LF, CR, LS, PS, NEL). */
export const LINE_BREAK: string = "(?:\\r\\n|\\r|\\n|\\u2028|\\u2029|\\u0085)";

/** Character class: not a line terminator (single-line slice). */
export const NOT_LINE: string = "[^\\r\\n\\u2028\\u2029\\u0085]";

/** Character class: not `>` and not a line terminator (angle-bracket email tokens). */
export const NOT_LINE_NOT_GT: string = "[^>\\r\\n\\u2028\\u2029\\u0085]";

/** Lookbehind: glued header follows prose/symbols — excludes whitespace-only glue. */
const GLUE_BEFORE_VON_FROM: string = "(?<=[\\p{L}\\p{N}.\\)!?])";

/** Dash count on each side of Original / Ursprüngliche Nachricht banners (inclusive). */
const ORIGINAL_MESSAGE_DASH_COUNT: string = "{1,10}";

const FLAGS_I: string = "i";
const FLAGS_IU: string = "iu";

function buildForwardingDelimiterRegexRulesInternal(): readonly RegExp[] {
  const LB: string = LINE_BREAK;
  return [
    new RegExp(
      `${LB}-{3,}\\s*Weitergeleitete Nachricht\\s*-{3,}\\s*${LB}`,
      FLAGS_I,
    ),
    new RegExp(`${LB}-{3,}\\s*Forwarded message\\s*-{3,}\\s*${LB}`, FLAGS_I),
    new RegExp(`${LB}Beginn der weitergeleiteten Nachricht\\s*${LB}`, FLAGS_I),
  ];
}

function buildReplyOnlyDelimiterRegexRulesInternal(): readonly RegExp[] {
  const LB: string = LINE_BREAK;
  const NL: string = NOT_LINE;
  const NG: string = NOT_LINE_NOT_GT;

  return [
    // Gmail / Apple Mail: "On Thu, 28 May 2026 at 09:32, name@x.de <name@x.de> wrote:"
    new RegExp(`(?:^|${LB})On .{1,800}?wrote:\\s*(?:${LB}|$)`, FLAGS_I),
    new RegExp(
      `${LB}${NL}{0,800}?<${NG}+@${NG}+>${NL}*(?:пише|пишет)\\s*:\\s*${LB}`,
      FLAGS_I,
    ),
    new RegExp(`${LB}Am\\b${NL}{0,800}?schrieb${NL}{0,500}:\\s*${LB}`, FLAGS_I),
    new RegExp(
      `(?:^|${LB})Am\\b${NL}{0,800}?\\b(?:NACHMITTAGS|VORMITTAGS)\\b${NL}{0,500}schrieb${NL}{0,200}:\\s*(?:${LB})?`,
      FLAGS_I,
    ),
    new RegExp(
      `${LB}${NL}*<${NG}+@${NG}+>${NL}*schrieb am\\b${NL}*:\\s*${LB}`,
      FLAGS_I,
    ),
    new RegExp(
      `${LB}-${ORIGINAL_MESSAGE_DASH_COUNT}\\s*Original Message\\s*-${ORIGINAL_MESSAGE_DASH_COUNT}\\s*${LB}`,
      FLAGS_I,
    ),
    new RegExp(
      `${LB}-${ORIGINAL_MESSAGE_DASH_COUNT}\\s*Ursprüngliche Nachricht\\s*-${ORIGINAL_MESSAGE_DASH_COUNT}\\s*${LB}`,
      FLAGS_I,
    ),
    new RegExp(
      `${LB}-${ORIGINAL_MESSAGE_DASH_COUNT}\\s*Ursprüngliche Nachricht\\s*-${ORIGINAL_MESSAGE_DASH_COUNT}\\s*${LB}Absender:\\s*.+${LB}+Gesendet:\\s*.+${LB}+An:\\s*.+${LB}+(?:Cc:\\s*.+${LB}+)?Thema:\\s*.+${LB}+`,
      FLAGS_I,
    ),
    new RegExp(
      `${LB}Von:\\s*(?:${NL}*<${NG}*@${NG}*>${NL}*|${LB}${NL}*<${NG}*@${NG}*>${NL}*)`,
      FLAGS_I,
    ),
    new RegExp(
      `${LB}From:\\s*(?:${NL}*<${NG}*@${NG}*>${NL}*|${LB}${NL}*<${NG}*@${NG}*>${NL}*)`,
      FLAGS_I,
    ),
    new RegExp(
      `${LB}Von:\\s*${LB}[\\s\\S]{0,800}?${LB}Datum:\\s*.+${LB}`,
      FLAGS_I,
    ),
    new RegExp(
      `${LB}From:\\s*${LB}[\\s\\S]{0,800}?${LB}Date:\\s*.+${LB}`,
      FLAGS_I,
    ),
    new RegExp(
      `${LB}Datum:\\s*.+${LB}An:\\s*.+${LB}Betreff:\\s*.+${LB}`,
      FLAGS_I,
    ),
    new RegExp(`${LB}Von:\\s*.+${LB}Gesendet:\\s*.+${LB}`, FLAGS_I),
    new RegExp(`${LB}From:\\s*.+${LB}Sent:\\s*.+${LB}`, FLAGS_I),
    new RegExp(`${LB}Von:${NL}*\\S+@\\S+`, FLAGS_I),
    new RegExp(`${LB}From:${NL}*\\S+@\\S+`, FLAGS_I),
    new RegExp(`${LB}_{20,}\\s*${LB}`, FLAGS_I),
    new RegExp(`${LB}${NL}*<${NG}+@${NG}+>${NL}*`, FLAGS_I),
    new RegExp(
      `${GLUE_BEFORE_VON_FROM}Von:\\s*.+${LB}Gesendet:\\s*.+${LB}`,
      FLAGS_IU,
    ),
    new RegExp(
      `${GLUE_BEFORE_VON_FROM}From:\\s*.+${LB}Sent:\\s*.+${LB}`,
      FLAGS_IU,
    ),
    new RegExp(
      `${GLUE_BEFORE_VON_FROM}Von:\\s*(?:${NL}*<${NG}*@${NG}*>${NL}*|${LB}${NL}*<${NG}*@${NG}*>${NL}*)`,
      FLAGS_IU,
    ),
    new RegExp(
      `${GLUE_BEFORE_VON_FROM}From:\\s*(?:${NL}*<${NG}*@${NG}*>${NL}*|${LB}${NL}*<${NG}*@${NG}*>${NL}*)`,
      FLAGS_IU,
    ),
    new RegExp(
      `(?<=[.!?])\\s+On .{1,800}?wrote:\\s*(?:${LB}|$)`,
      FLAGS_IU,
    ),
  ];
}

/** Plain-text quote marker: a line starting with one or more `>` (RFC 3676 style). */
export const QUOTED_LINE_RE: RegExp = /^>+(?:\s|$)/;

/**
 * Line that introduces quoted history above a `>` block: attribution verbs,
 * forwarding banners, and the header fields that open an Outlook block.
 *
 * Deliberately excludes the continuation fields (`An:`, `Betreff:`, `Thema:`,
 * `Absender:`, …): a sender may legitimately start a line with them, and a real
 * Outlook header block is already caught here by `Von:`/`From:`/`Gesendet:`.
 */
export const QUOTE_HEADER_LINE_RE: RegExp =
  /(?:schrieb\s*:|wrote:|пише\s*:|пишет\s*:|^\s*(?:Von|From|Gesendet|Sent|Datum|Date)\s*:|Ursprüngliche Nachricht|Original Message|Weitergeleitete Nachricht|Forwarded message|Beginn der weitergeleiteten Nachricht)/iu;

/** Frozen delimiter patterns — minimum match index wins in consumers. */
export const REPLY_DELIMITER_REGEX_RULES: readonly RegExp[] = [
  ...buildReplyOnlyDelimiterRegexRulesInternal(),
  ...buildForwardingDelimiterRegexRulesInternal(),
];

/**
 * UI-only subset: excludes forwarding markers so outbound forwards are not cut in HTML.
 * Still consumed only through {@link computeEmailVisiblePlainCut} (`mode: "ui"`).
 */
export const UI_REPLY_QUOTE_DELIMITER_REGEX_RULES: readonly RegExp[] =
  buildReplyOnlyDelimiterRegexRulesInternal();
